import { useState, useEffect } from 'react';

/**
 * Les balises <img>/<video> ne peuvent pas envoyer l'en-tête Authorization
 * (contrairement à fetch(), déjà intercepté globalement dans index.js pour y
 * ajouter le jeton Entra du praticien connecté) -- donc toute image/vidéo
 * chargée directement via src={urlBackend} a cessé de fonctionner dès que
 * les routes de lecture ont commencé à exiger une authentification. Ce hook
 * fait le fetch() à la place (qui, lui, passe par l'intercepteur et reçoit
 * le jeton), récupère le blob, et fournit une URL objet locale utilisable
 * comme src="...".
 */
// Une photo tout juste uploadée peut ne pas être immédiatement lisible côté
// stockage (courte fenêtre de latence) : sans ré-essai, le premier fetch
// échoue et le statut reste bloqué en 'error' jusqu'à ce que quelque chose
// refasse tourner cet effet -- en pratique, un rechargement complet de la
// page. 2 ré-essais rapprochés (300ms, 800ms) absorbent cette fenêtre sans
// donner l'impression d'un chargement anormalement long pour le cas normal
// (où le premier essai réussit déjà).
const RETRY_DELAYS_MS = [300, 800];

export function useAuthedImage(url) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let timer = null;
    setStatus('loading');
    setBlobUrl(null);
    if (!url) return undefined;

    const tryFetch = (attempt) => {
      fetch(url)
        .then(res => {
          if (!res.ok) {
            const err = new Error(`HTTP ${res.status}`);
            err.status = res.status;
            throw err;
          }
          return res.blob();
        })
        .then(blob => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setStatus('loaded');
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < RETRY_DELAYS_MS.length) {
            timer = setTimeout(() => tryFetch(attempt + 1), RETRY_DELAYS_MS[attempt]);
          } else {
            setStatus('error');
          }
        });
    };
    tryFetch(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { status, blobUrl };
}

/**
 * Variante "polling" : réessaie périodiquement tant que la réponse est un
 * 404 (image en cours de génération en arrière-plan côté backend), au lieu
 * d'abandonner immédiatement. S'arrête après maxAttempts et passe en
 * 'failed'.
 */
export function useAuthedImagePolling(url, { intervalMs = 5000, maxAttempts = 60 } = {}) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'failed'
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let timer = null;
    let attempt = 0;
    setStatus('loading');
    setBlobUrl(null);
    if (!url) return undefined;

    const tryFetch = () => {
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(blob => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setStatus('loaded');
        })
        .catch(() => {
          if (cancelled) return;
          attempt += 1;
          if (attempt < maxAttempts) {
            timer = setTimeout(tryFetch, intervalMs);
          } else {
            setStatus('failed');
          }
        });
    };
    tryFetch();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, intervalMs, maxAttempts]);

  return { status, blobUrl };
}

/**
 * Essaie plusieurs URLs candidates dans l'ordre (ex: vue sagittale en
 * priorité, puis frontale) et s'arrête à la première qui répond -- sans
 * polling, un seul passage.
 */
export function useAuthedImageFirstMatch(urls) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const key = (urls || []).join('|');

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setBlobUrl(null);
    setFailed(false);

    const tryAt = (i) => {
      if (!urls || i >= urls.length) {
        if (!cancelled) setFailed(true);
        return;
      }
      fetch(urls[i])
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(blob => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        })
        .catch(() => { if (!cancelled) tryAt(i + 1); });
    };
    tryAt(0);

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key, urls]);

  return { blobUrl, failed };
}
